import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { getMyBranches } from '../../auth/api/authApi';
import listStyles from '../../shared/components/ListDetail.module.css';
import primitives from '../../../styles/primitives.module.css';
import styles from './Branches.module.css';

const STATUS_KEYS = {
  active: 'branchesStatusActive',
  inactive: 'branchesStatusInactive',
};

export const BranchSelector = ({
  token,
  t,
  variant = 'page',
  activeBranch,
  onSelect,
}) => {
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isSelecting, setIsSelecting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuWrapRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!token) {
      setBranches([]);
      setIsLoading(false);
      return undefined;
    }

    let isActive = true;
    setIsLoading(true);
    setLoadError('');

    getMyBranches(token)
      .then((response) => {
        if (!isActive) return;
        setBranches(Array.isArray(response?.items) ? response.items : []);
      })
      .catch(() => {
        if (!isActive) return;
        setLoadError(t('branchSelectorLoadFailed'));
        setBranches([]);
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [token, t]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (variant !== 'dropdown' || !menuOpen) return undefined;

    const onDocPointerDown = (e) => {
      const el = menuWrapRef.current;
      if (el && !el.contains(e.target)) {
        closeMenu();
      }
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };

    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [variant, menuOpen, closeMenu]);

  const handleSelect = async (branchId) => {
    if (isSelecting || branchId == null) return;
    setIsSelecting(true);
    try {
      await onSelect(branchId);
      closeMenu();
    } finally {
      setIsSelecting(false);
    }
  };

  const selectedId = activeBranch?.id ?? null;
  const triggerLabel = activeBranch?.name?.trim() || t('navActiveBranch');

  if (isLoading) {
    return (
      <div className={variant === 'dropdown' ? styles.dropdownLoading : listStyles.listLoading}>
        {t('branchSelectorLoading')}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={variant === 'dropdown' ? styles.dropdownError : listStyles.listEmpty}>
        {loadError}
      </div>
    );
  }

  if (!branches.length) {
    return (
      <div className={variant === 'dropdown' ? styles.dropdownEmpty : listStyles.listEmpty}>
        {t('branchSelectorEmpty')}
      </div>
    );
  }

  const renderBranchButton = (branch, compact) => {
    const statusKey = STATUS_KEYS[branch.status] || branch.status;
    const selected = branch.id === selectedId;
    const buttonClass = compact
      ? `${styles.dropdownItem} ${selected ? styles.dropdownItemSelected : ''}`
      : `${listStyles.row} ${selected ? listStyles.rowSelected : ''}`;

    return (
      <button
        key={branch.id}
        type="button"
        className={buttonClass}
        disabled={isSelecting}
        aria-current={selected ? 'true' : undefined}
        onClick={() => handleSelect(branch.id)}
      >
        <div className={compact ? styles.dropdownItemMain : listStyles.rowMain}>
          <span className={compact ? styles.dropdownItemTitle : listStyles.rowTitle}>
            {branch.name}
          </span>
          <div className={compact ? styles.dropdownItemBadges : listStyles.rowBadges}>
            <span
              className={branch.status === 'active' ? primitives.badgeAccent : listStyles.badge}
            >
              {t(statusKey)}
            </span>
          </div>
        </div>
      </button>
    );
  };

  if (variant === 'dropdown') {
    return (
      <div className={styles.dropdownWrap} ref={menuWrapRef}>
        <button
          type="button"
          className={styles.dropdownTrigger}
          aria-expanded={menuOpen}
          aria-haspopup="listbox"
          aria-controls={menuId}
          aria-label={t('navActiveBranch')}
          disabled={isSelecting}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className={styles.dropdownTriggerLabel}>{triggerLabel}</span>
          <span className={`${styles.dropdownChevron} ${menuOpen ? styles.dropdownChevronOpen : ''}`} aria-hidden />
        </button>
        {menuOpen ? (
          <div id={menuId} className={styles.dropdownPanel} role="listbox" aria-label={t('branchSelectorTitle')}>
            {branches.map((branch) => renderBranchButton(branch, true))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={listStyles.listCard}>
      <div className={listStyles.listHeader}>
        <span>{t('branchSelectorListLabel')}</span>
        <span className={listStyles.listTotal}>
          {t('branchesTotal')} {branches.length}
        </span>
      </div>
      <div className={listStyles.listItems}>
        {branches.map((branch) => renderBranchButton(branch, false))}
      </div>
      {isSelecting ? (
        <p className={styles.formError}>{t('branchSelectorSelecting')}</p>
      ) : null}
    </div>
  );
};
